/* eslint-disable vue/no-reserved-component-names */

import directives from '@/common/directives'
import { i18n, createPrimeVueLocale } from '@/common/i18n'
import axiosPlugin from '@/common/plugins/axios'
import snackbarPlugin from '@/common/plugins/snackbar'
import { useUserStore } from '@/stores/user'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import App from './App.vue'
import { configService } from './common/utils/clientConfig'
import { shouldBlockMobileViewport } from './common/utils/mobileDesktopOnly'
import router from './router'

// Components
import Accordion from 'primevue/accordion'
import AccordionContent from 'primevue/accordioncontent'
import AccordionHeader from 'primevue/accordionheader'
import AccordionPanel from 'primevue/accordionpanel'
import AutoComplete from 'primevue/autocomplete'
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import Chip from 'primevue/chip'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import DatePicker from 'primevue/datepicker'
import Dialog from 'primevue/dialog'
import FileUpload from 'primevue/fileupload'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Paginator from 'primevue/paginator'
import Password from 'primevue/password'
import Popover from 'primevue/popover'
import ProgressSpinner from 'primevue/progressspinner'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import Tree from 'primevue/tree'
import TreeTable from 'primevue/treetable'
import FloatLabel from 'primevue/floatlabel';
import ToastService from 'primevue/toastservice';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';

// PrimeVue
import PrimeVue, { type PrimeVueConfiguration } from 'primevue/config'
import Ripple from 'primevue/ripple'
import { primeVueThemeOptions, resolveThemePreset } from './theme/resolveThemePreset'

// Styles
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import './assets/main.css'
import './assets/ui-scale.css'
import './assets/themes/spd.css'
import { initOverlayPositionSync } from './common/utils/overlayPositionSync'
import { initUiScale } from './common/utils/uiScale'

import { clearAllCookies } from '@/common/plugins/axios'
import { installErrorReporter } from '@/common/utils/errorReporter'

const isMswEnabled = import.meta.env.VITE_MSW === 'true'

const initAuth = async () => {
  const userStore = useUserStore()
  try {
    configService.reload()

    const authRestored = await userStore.initAuth()

    if (!authRestored) {
      const hasSessionCookies = document.cookie.includes('JSESSIONID=') || document.cookie.includes('CSRF-TOKEN=')
      if (!hasSessionCookies) {
        clearAllCookies()
      }
    }
  } catch (error) {
    console.error('Auth initialization error:', error)
    const hasSessionCookies = document.cookie.includes('JSESSIONID=') || document.cookie.includes('CSRF-TOKEN=')
    if (!hasSessionCookies) {
      clearAllCookies()
    }
  }
}

const bootstrap = async () => {
  if (isMswEnabled) {
    console.log('MSW: starting mock service worker (VITE_MSW=true)')
    const { worker } = await import('./mocks/browser')
    await worker.start({
      onUnhandledRequest: 'bypass', // не логировать и пропускать дальше
    })
  }

  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  configService.applyClientThemeToDocument()
  initUiScale()
  initOverlayPositionSync()

  // Регистрация компонентов PrimeVue
  app.component('InputText', InputText)
  app.component('Button', Button)
  app.component('Badge', Badge)
  app.component('Password', Password)
  app.component('Checkbox', Checkbox)
  app.component('Select', Select)
  app.component('Textarea', Textarea)
  app.component('AutoComplete', AutoComplete)
  app.component('Chip', Chip)
  app.component('DatePicker', DatePicker)
  app.component('Dialog', Dialog)
  app.component('Accordion', Accordion)
  app.component('AccordionPanel', AccordionPanel)
  app.component('AccordionHeader', AccordionHeader)
  app.component('AccordionContent', AccordionContent)
  app.component('Popover', Popover)
  app.component('FileUpload', FileUpload)
  app.component('ProgressSpinner', ProgressSpinner)
  app.component('MultiSelect', MultiSelect)
  app.component('Paginator', Paginator)
  app.component('Tree', Tree)
  app.component('DataTable', DataTable)
  app.component('Column', Column)
  app.component('TreeTable', TreeTable)
  app.component('FloatLabel', FloatLabel)
  app.component('Splitter', Splitter)
  app.component('SplitterPanel', SplitterPanel)

  app.use(i18n)
  const primeVueLocale = createPrimeVueLocale()
  app.use(PrimeVue, {
    locale: primeVueLocale,
    theme: {
      preset: resolveThemePreset(),
      options: primeVueThemeOptions,
    },
    zIndex: {
      overlay: 12000,
      menu: 12000,
      modal: 13000,
      tooltip: 14000,
    },
    ripple: true,
  } as PrimeVueConfiguration)

  // Register PrimeVue directives
  app.directive('ripple', Ripple)
  app.use(ToastService)
  app.use(router)
  app.use(axiosPlugin)
  app.use(snackbarPlugin)
  app.use(directives)

  // Add global properties
  app.config.globalProperties.$config = configService

  installErrorReporter(app)

  if (!shouldBlockMobileViewport()) {
    await initAuth()
  }
  app.mount('#app')
}

bootstrap()
